import React, { useEffect, useRef } from "react";
import { Box, Stack, Typography } from "@mui/material";
import {
  CustomTextInput,
  CustomSectionTitle,
  CustomSingleSelect,
  CustomCalendarInput,
  colors,
} from "@ippon/shared-components";
import type { UseFormReturn } from "react-hook-form";
import { squareMetersToTsubo } from "@ippon/shared-utils";
import {
  LAND_TYPE_OPTIONS,
  PAVING_TYPE_OPTIONS,
  PARKING_SUBTYPE_OPTIONS,
} from "@ippon/shared-types";
import type { UnitFormValues } from "../UnitTabForm";
import UnitSectionCustomFields from "./UnitSectionCustomFields";
import EquipmentSpecsInput from "./EquipmentSpecsInput";
import LabeledCheckbox from "./LabeledCheckbox";
import UnitOtherDocuments from "./UnitOtherDocuments";

interface Props {
  form: UseFormReturn<UnitFormValues>;
  labelWidth?: string;
  inputWidth?: string;
}

const DIRECTION_OPTIONS = [
  "南",
  "南東",
  "東",
  "北東",
  "北",
  "北西",
  "西",
  "南西",
].map((v) => ({ label: v, value: v }));

/** react-hook-form の number 入力は文字列になることがある */
const parseFormNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const addYears = (yyyymm: string, years: number): string => {
  const [y, m] = yyyymm.split("-").map((v) => parseInt(v, 10));
  if (!y || !m) return yyyymm;
  return `${y + years}-${String(m).padStart(2, "0")}`;
};

const toMonthPickerValue = (value: unknown): string => {
  if (typeof value !== "string" || !value) return "";
  return /^\d{4}-\d{2}$/.test(value) ? `${value}-01` : value;
};

const MonthCalendarInput: React.FC<{
  form: UseFormReturn<UnitFormValues>;
  name:
    | "facility_detail.manufactured_at"
    | "facility_detail.installed_at"
    | "facility_detail.next_replacement_at";
  label: string;
  labelWidth?: string;
  inputWidth?: string;
}> = ({ form, name, label, labelWidth, inputWidth }) => {
  const value = form.watch(name);
  const pickerName = `__month_picker_${name.replace(/\./g, "_")}`;

  useEffect(() => {
    const pickerValue = toMonthPickerValue(value);
    if (form.getValues(pickerName as any) !== pickerValue) {
      form.setValue(pickerName as any, pickerValue, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [form, name, pickerName, value]);

  useEffect(() => {
    const sub = form.watch((values, info) => {
      if (info.name !== pickerName) return;
      const raw = String(
        (values as unknown as Record<string, unknown>)[pickerName] ?? "",
      );
      const next = raw ? raw.slice(0, 7) : "";
      if (form.getValues(name) === next) return;
      form.setValue(name, next, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    });
    return () => sub.unsubscribe();
  }, [form, name, pickerName]);

  return (
    <CustomCalendarInput
      name={pickerName as any}
      control={form.control as any}
      label={label}
      labelWidth={labelWidth}
      inputWidth={inputWidth}
      views={["year", "month"]}
      openTo="month"
      format="YYYY/MM"
      outputFormat="YYYY-MM-DD"
    />
  );
};

/**
 * 詳細情報セクション (見出しは常に「詳細情報」固定)。
 *
 * 場所区分ごとに完全に異なる固定フィールド + 添付エリアを内包する:
 * - 専有部 (private_detail): 階数/面積/坪数/天井高/向き/設置情報/光熱費徴収
 *   ※区画画像は外側 SectionTabForm で扱う (data: private_detail.images)
 * - 共用部 (common_detail): 利用用途/階数/面積/坪数/天井高/向き/設置情報/関連資料
 * - 建物設備 (facility_detail): メーカー/型番/製造年月/設置年月/定格容量/
 *   耐用年数/次回交換予定年月/関連資料
 * - 敷地 (land_detail): 種別 → 駐車場 / 外構・緑地 / その他 で出し分け
 * - その他: カスタム項目のみ
 *
 * 末尾に section_key="detail" のカスタムフィールドを描画する。
 */
const DetailSection: React.FC<Props> = ({ form, labelWidth, inputWidth }) => {
  const { register, control, watch, setValue, formState } = form;
  const widthProps = { labelWidth, inputWidth } as const;
  const areaType = watch("area_type");

  // 専有部: 面積 → 坪数 自動計算。
  // 初期表示時の既存坪数は保持し、面積を変更した時だけ連動させる。
  const privateSqm = watch("private_detail.area_sqm");
  const privateTsubo = watch("private_detail.area_tsubo");
  const prevPrivateSqmRef = useRef(privateSqm);
  useEffect(() => {
    if (areaType !== "private") {
      prevPrivateSqmRef.current = privateSqm;
      return;
    }
    const sqmChanged = prevPrivateSqmRef.current !== privateSqm;
    prevPrivateSqmRef.current = privateSqm;
    const sqm = parseFormNumber(privateSqm);
    const cur = parseFormNumber(privateTsubo);
    if (sqm === undefined) {
      if (sqmChanged && cur !== undefined) {
        setValue("private_detail.area_tsubo", undefined, { shouldDirty: false });
      }
      return;
    }
    if (!sqmChanged && cur !== undefined) return;
    const calc = squareMetersToTsubo(sqm);
    if (cur === calc) return; // 既に一致なら何もしない (無限ループ防止)
    setValue("private_detail.area_tsubo", calc, { shouldDirty: false });
  }, [privateSqm, privateTsubo, areaType, setValue]);

  // 共用部: 面積 → 坪数 自動計算
  const commonSqm = watch("common_detail.area_sqm");
  const commonTsubo = watch("common_detail.area_tsubo");
  const prevCommonSqmRef = useRef(commonSqm);
  useEffect(() => {
    if (areaType !== "common") {
      prevCommonSqmRef.current = commonSqm;
      return;
    }
    const sqmChanged = prevCommonSqmRef.current !== commonSqm;
    prevCommonSqmRef.current = commonSqm;
    const sqm = parseFormNumber(commonSqm);
    const cur = parseFormNumber(commonTsubo);
    if (sqm === undefined) {
      if (sqmChanged && cur !== undefined) {
        setValue("common_detail.area_tsubo", undefined, { shouldDirty: false });
      }
      return;
    }
    if (!sqmChanged && cur !== undefined) return;
    const calc = squareMetersToTsubo(sqm);
    if (cur === calc) return;
    setValue("common_detail.area_tsubo", calc, { shouldDirty: false });
  }, [commonSqm, commonTsubo, areaType, setValue]);

  // 建物設備: 設置年月 + 耐用年数 → 次回交換予定年月 自動算出
  const installedAt = watch("facility_detail.installed_at");
  const usefulLife = watch("facility_detail.useful_life_years");
  const nextReplacement = watch("facility_detail.next_replacement_at");
  const prevFacilityScheduleRef = useRef({ installedAt, usefulLife });
  useEffect(() => {
    if (areaType !== "facility") {
      prevFacilityScheduleRef.current = { installedAt, usefulLife };
      return;
    }
    const scheduleChanged =
      prevFacilityScheduleRef.current.installedAt !== installedAt ||
      prevFacilityScheduleRef.current.usefulLife !== usefulLife;
    prevFacilityScheduleRef.current = { installedAt, usefulLife };
    if (!scheduleChanged) return;
    const parsedUsefulLife = parseFormNumber(usefulLife);
    if (installedAt && parsedUsefulLife !== undefined) {
      setValue(
        "facility_detail.next_replacement_at",
        addYears(installedAt, parsedUsefulLife),
        { shouldDirty: false },
      );
    } else if (nextReplacement) {
      setValue("facility_detail.next_replacement_at", undefined, {
        shouldDirty: false,
      });
    }
  }, [installedAt, usefulLife, nextReplacement, areaType, setValue]);

  const landType = watch("land_detail.land_type");
  const utility = watch("private_detail.utility_collection");

  // 種別 (land_type) を切り替えたら、前の種別に属するフィールドをクリアする。
  // UI で非表示になっただけでは react-hook-form 内部に値が残り、保存時に
  // DB へ書き出されてしまうため (例: 駐車場で lot_number 入力後、外構へ切替 → 保存
  // すると lot_number も一緒に永続化される)。
  const prevLandTypeRef = useRef(landType);
  useEffect(() => {
    if (areaType !== "land") return;
    const prev = prevLandTypeRef.current;
    if (prev === landType) return;
    if (prev === "parking") {
      setValue("land_detail.lot_number", undefined, { shouldDirty: true });
      setValue("land_detail.parking_subtype", undefined, { shouldDirty: true });
      setValue("land_detail.height_limit_m", undefined, { shouldDirty: true });
      setValue("land_detail.width_limit_m", undefined, { shouldDirty: true });
    } else if (prev === "exterior" || prev === "greenery") {
      setValue("land_detail.paving_type", undefined, { shouldDirty: true });
      setValue("land_detail.last_repair_at", undefined, { shouldDirty: true });
    }
    prevLandTypeRef.current = landType;
  }, [landType, areaType, setValue]);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <CustomSectionTitle title="詳細情報" size="small" />
      </Box>
      <Stack spacing={0}>
        {areaType === "private" && (
          <>
            <CustomTextInput
              label="階数"
              name="private_detail.floor"
              register={register}
              errors={formState.errors}
              placeholder="例: 3F / B1 / 2-3F"
              {...widthProps}
            />
            <CustomTextInput
              label="面積"
              name="private_detail.area_sqm"
              type="number"
              register={register}
              errors={formState.errors}
              showMeter
              {...widthProps}
            />
            <CustomTextInput
              label="坪数"
              name="private_detail.area_tsubo"
              type="number"
              register={register}
              errors={formState.errors}
              showTsubo
              placeholder="自動計算"
              {...widthProps}
            />
            <CustomTextInput
              label="天井高 (m)"
              name="private_detail.ceiling_height_m"
              type="number"
              register={register}
              errors={formState.errors}
              {...widthProps}
            />
            <CustomSingleSelect
              label="向き"
              name="private_detail.direction"
              control={control}
              errors={formState.errors}
              options={DIRECTION_OPTIONS}
              {...widthProps}
            />
            <EquipmentSpecsInput
              name="private_detail.equipment_specs"
              control={control}
              setValue={setValue}
              register={register}
              errors={formState.errors}
              {...widthProps}
            />
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: colors.text.primary,
                  mb: 1,
                }}
              >
                光熱費徴収
              </Typography>
              <Stack
                direction="row"
                spacing={3}
                alignItems="center"
                flexWrap="wrap"
              >
                <LabeledCheckbox
                  label="電気料金徴収あり"
                  checked={!!utility?.electricity}
                  onChange={(v) =>
                    setValue(
                      "private_detail.utility_collection",
                      { ...(utility ?? {}), electricity: v },
                      { shouldDirty: true },
                    )
                  }
                />
                <LabeledCheckbox
                  label="水道料金徴収あり"
                  checked={!!utility?.water}
                  onChange={(v) =>
                    setValue(
                      "private_detail.utility_collection",
                      { ...(utility ?? {}), water: v },
                      { shouldDirty: true },
                    )
                  }
                />
                <LabeledCheckbox
                  label="ガス料金徴収あり"
                  checked={!!utility?.gas}
                  onChange={(v) =>
                    setValue(
                      "private_detail.utility_collection",
                      { ...(utility ?? {}), gas: v },
                      { shouldDirty: true },
                    )
                  }
                />
              </Stack>
            </Box>
            <UnitSectionCustomFields
              form={form}
              sectionKey="detail"
              {...widthProps}
            />
          </>
        )}

        {areaType === "common" && (
          <>
            <CustomTextInput
              label="利用用途"
              name="common_detail.usage_description"
              register={register}
              errors={formState.errors}
              multiline
              rows={3}
              {...widthProps}
            />
            <CustomTextInput
              label="階数"
              name="common_detail.floor"
              register={register}
              errors={formState.errors}
              placeholder="例: 3F / B1 / 2-3F"
              {...widthProps}
            />
            <CustomTextInput
              label="面積"
              name="common_detail.area_sqm"
              type="number"
              register={register}
              errors={formState.errors}
              showMeter
              {...widthProps}
            />
            <CustomTextInput
              label="坪数"
              name="common_detail.area_tsubo"
              type="number"
              register={register}
              errors={formState.errors}
              showTsubo
              placeholder="自動計算"
              {...widthProps}
            />
            <CustomTextInput
              label="天井高 (m)"
              name="common_detail.ceiling_height_m"
              type="number"
              register={register}
              errors={formState.errors}
              {...widthProps}
            />
            <CustomSingleSelect
              label="向き"
              name="common_detail.direction"
              control={control}
              errors={formState.errors}
              options={DIRECTION_OPTIONS}
              {...widthProps}
            />
            <EquipmentSpecsInput
              name="common_detail.equipment_specs"
              control={control}
              setValue={setValue}
              register={register}
              errors={formState.errors}
              {...widthProps}
            />
            <UnitSectionCustomFields
              form={form}
              sectionKey="detail"
              {...widthProps}
            />
            <Box sx={{ mt: 2 }}>
              <UnitOtherDocuments
                title="関連資料"
                documents={form.watch("common_detail.documents") || []}
                onChange={(next) =>
                  form.setValue("common_detail.documents", next, {
                    shouldDirty: true,
                  })
                }
                inputWidth={inputWidth}
              />
            </Box>
          </>
        )}

        {areaType === "facility" && (
          <>
            <CustomTextInput
              label="メーカー"
              name="facility_detail.manufacturer"
              register={register}
              errors={formState.errors}
              {...widthProps}
            />
            <CustomTextInput
              label="型番・品番"
              name="facility_detail.model_number"
              register={register}
              errors={formState.errors}
              {...widthProps}
            />
            <MonthCalendarInput
              form={form}
              label="製造年月"
              name="facility_detail.manufactured_at"
              {...widthProps}
            />
            <MonthCalendarInput
              form={form}
              label="設置年月"
              name="facility_detail.installed_at"
              {...widthProps}
            />
            <CustomTextInput
              label="定格・容量"
              name="facility_detail.capacity"
              register={register}
              errors={formState.errors}
              {...widthProps}
            />
            <CustomTextInput
              label="耐用年数"
              name="facility_detail.useful_life_years"
              type="number"
              register={register}
              errors={formState.errors}
              endAdornmentText="年"
              {...widthProps}
            />
            <MonthCalendarInput
              form={form}
              label="次回交換予定年月"
              name="facility_detail.next_replacement_at"
              {...widthProps}
            />
            <UnitSectionCustomFields
              form={form}
              sectionKey="detail"
              {...widthProps}
            />
            <Box sx={{ mt: 2 }}>
              <UnitOtherDocuments
                title="関連資料"
                documents={form.watch("facility_detail.documents") || []}
                onChange={(next) =>
                  form.setValue("facility_detail.documents", next, {
                    shouldDirty: true,
                  })
                }
                inputWidth={inputWidth}
              />
            </Box>
          </>
        )}

        {areaType === "land" && (
          <>
            <CustomSingleSelect
              label="種別"
              name="land_detail.land_type"
              control={control}
              errors={formState.errors}
              options={[...LAND_TYPE_OPTIONS]}
              placeholder="選択してください"
              isRequired
              {...widthProps}
            />
            {landType === "parking" && (
              <>
                <CustomTextInput
                  label="車室番号"
                  name="land_detail.lot_number"
                  register={register}
                  errors={formState.errors}
                  {...widthProps}
                />
                <CustomSingleSelect
                  label="駐車場種別"
                  name="land_detail.parking_subtype"
                  control={control}
                  errors={formState.errors}
                  options={[...PARKING_SUBTYPE_OPTIONS]}
                  {...widthProps}
                />
                <CustomTextInput
                  label="車高制限"
                  name="land_detail.height_limit_m"
                  type="number"
                  register={register}
                  errors={formState.errors}
                  showMeter
                  {...widthProps}
                />
                <CustomTextInput
                  label="車幅制限"
                  name="land_detail.width_limit_m"
                  type="number"
                  register={register}
                  errors={formState.errors}
                  showMeter
                  {...widthProps}
                />
              </>
            )}
            {(landType === "exterior" || landType === "greenery") && (
              <>
                <CustomSingleSelect
                  label="舗装種別"
                  name="land_detail.paving_type"
                  control={control}
                  errors={formState.errors}
                  options={[...PAVING_TYPE_OPTIONS]}
                  {...widthProps}
                />
                <CustomTextInput
                  label="最終補修年月"
                  name="land_detail.last_repair_at"
                  register={register}
                  errors={formState.errors}
                  placeholder="YYYY/MM"
                  {...widthProps}
                />
              </>
            )}
            <UnitSectionCustomFields
              form={form}
              sectionKey="detail"
              {...widthProps}
            />
          </>
        )}

        {areaType === "other" && (
          <UnitSectionCustomFields
            form={form}
            sectionKey="detail"
            {...widthProps}
          />
        )}
      </Stack>
    </Box>
  );
};

export default DetailSection;
